import {ActionList, type ActionListProps} from '@primer/react'

import type {IPickerItem} from '../types'

interface RenderingProps<T extends IPickerItem> {
  onRenderItemName(item: T): React.ReactNode
  onRenderItemDescription?(item: T): React.ReactNode
  onRenderItemLeadingVisual?(item: T): React.ReactNode
  onRenderItemTrailingVisual?(item: T): React.ReactNode
  onRenderItemDisabled?(item: T): boolean
}

export interface PickerListProps<T extends IPickerItem> extends RenderingProps<T> {
  selectionVariant: ActionListProps['selectionVariant']
  items: T[]
  selectedItems: T[]
  setSelectedItems(items: T[]): void
  title: string
}

export function PickerList<T extends IPickerItem>({
  selectionVariant,
  items,
  selectedItems,
  setSelectedItems,
  title,
  ...renderProps
}: PickerListProps<T>) {
  const selectedIds = new Set(selectedItems.map(item => item.id))

  const onSelectItem = (item: T) => {
    const isSelected = selectedIds.has(item.id)

    if (isSelected) {
      setSelectedItems(selectedItems.filter(prev => prev.id !== item.id))
    } else {
      setSelectedItems(selectionVariant === 'single' ? [item] : [...selectedItems, item])
    }
  }

  return (
    <ActionList selectionVariant={selectionVariant} role="listbox" aria-label={title}>
      {items.map(item => (
        <PickerListItem
          key={item.id}
          role="option"
          selected={selectedIds.has(item.id)}
          onSelect={() => onSelectItem(item)}
          item={item}
          {...renderProps}
        />
      ))}
    </ActionList>
  )
}

interface PickerListItemProps<T extends IPickerItem> extends RenderingProps<T> {
  role: 'option' | 'listitem'
  item: T
  selected?: boolean
  onSelect?(): void
}

export function PickerListItem<T extends IPickerItem>(props: PickerListItemProps<T>) {
  const {
    role,
    item,
    selected = false,
    onSelect,
    onRenderItemName,
    onRenderItemDescription,
    onRenderItemLeadingVisual,
    onRenderItemTrailingVisual,
    onRenderItemDisabled,
  } = props
  const description = onRenderItemDescription ? onRenderItemDescription(item) : null
  return (
    <ActionList.Item role={role} selected={selected} onSelect={onSelect} disabled={onRenderItemDisabled?.(item)}>
      {onRenderItemLeadingVisual && (
        <ActionList.LeadingVisual>{onRenderItemLeadingVisual(item)}</ActionList.LeadingVisual>
      )}
      {onRenderItemName(item)}
      {!!description && <ActionList.Description variant="inline">{description}</ActionList.Description>}
      {onRenderItemTrailingVisual && (
        <ActionList.TrailingVisual>{onRenderItemTrailingVisual(item)}</ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )
}
